import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import * as padStart from 'lodash/padStart';

import { LandingSurface, LandingSurfaceType, LandingSurfaceCondition } from '../models/landing-surface.model';
import { ApproachLighting } from '../models/approach-lighting.model';
import { TakeoffLandArea, TrafficPatternDirection, RunwayMarkingsCondition } from '../models/takeoff-land-area.model';
import { isNumeric } from '../../util/helpers';
import { Obstacle } from '../models/obstacle.model';
import { AirportComment, CommentType } from '../models/airport-comment.model';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { EditRemarkComponent } from '../edit-remark/edit-remark.component';
import { EditRunwayComponent } from '../edit-runway/edit-runway.component';
import { DeleteRemarkComponent } from '../delete-remark/delete-remark.component';
import { MatDialog, MatDialogConfig, MatSnackBar } from '@angular/material';
import { AirportService } from '../airport.service';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { DeleteRunwayComponent } from '../delete-runway/delete-runway.component';
import { EditTakeoffLandAreaComponent } from '../edit-takeoff-land-area/edit-takeoff-land-area.component';
import { EditAreaObstacleComponent } from '../edit-area-obstacle/edit-area-obstacle.component';
import { DeleteAreaObstacleComponent } from '../delete-area-obstacle/delete-area-obstacle.component';
import { DeleteTakeoffLandAreaComponent } from '../delete-takeoff-land-area/delete-takeoff-land-area.component';
import { DeleteAreaLightingComponent } from '../delete-area-lighting/delete-area-lighting.component';
import { EditAreaLightingComponent } from '../edit-area-lighting/edit-area-lighting.component';


@Component({
  selector: 'app-runway-detail',
  templateUrl: './runway-detail.component.html',
  styleUrls: ['./runway-detail.component.scss']
})
export class RunwayDetailComponent implements OnInit {
  @Input() airportId: number;
  @Input() identifier: string;
  @Input() runway: LandingSurface;
  @Input() magVariation: number;
  @Input() remarks: Array<AirportComment>;
  @Input() isIslandSource: boolean;
  @Output() runwayDeleted = new EventEmitter<number>();
  Math = Math;
  TrafficPatternDirection = TrafficPatternDirection;
  LandingSurfaceType = LandingSurfaceType;
  @Input() editModeSet: boolean;
  isFlightDeckAdmin: boolean;

  constructor (
    private _businessManagerService: BusinessManagerService,
    private _decimalPipe: DecimalPipe,
    private _toggleService: EditToggleService,
    private _editRemarkDialog: MatDialog,
    private _editRunwayDialog: MatDialog,
    private _deleteRemarkDialog: MatDialog,
    public _snackBar: MatSnackBar,
    private _airportService: AirportService,
    private _deleteRunwayDialog: MatDialog,
    private _editTakeoffLandAreaDialog: MatDialog,
    private _deleteTakeoffLandAreaDialog: MatDialog,
    private _editObstacleDialog: MatDialog,
    private _deleteObstacleDialog: MatDialog
  ) {}

  async ngOnInit() {
    this.isFlightDeckAdmin = await this._businessManagerService.isFlightDeckAdmin();
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
  }

  get surfaceWarning(): boolean {
    return !this.runway.paved || this.runway.condition === LandingSurfaceCondition.Fair;
  }

  get surfaceDanger(): boolean {
    return this.runway.condition === LandingSurfaceCondition.Poor
      ||  this.runway.condition === LandingSurfaceCondition.Failed;
  }

  formatHeading(heading: number): string {
    return isNumeric(heading) ? padStart(heading, 3, '0') : '';
  }

  isNumeric(value: any): boolean {
    return isNumeric(value);
  }

  get type(): string {
    return LandingSurface.formatLandingSurfaceType(this.runway.type);
  }

  get edgeLighting(): string {
    return LandingSurface.formatEdgeLighting(this.runway.edgeLighting);
  }

  getMagneticHeading(area: TakeoffLandArea): string {
    if (isNumeric(area.trueAlignment) && this.isNumeric(this.magVariation)) {
      return this.formatHeading(area.trueAlignment + this.magVariation);
    }
    return '';
  }

  getGradientDirection(gradient: number): string {
    if (!isNumeric(gradient)) {
      return '';
    }
    const sign = Math.sign(gradient);
    switch (sign) {
      case 1:
        return 'up';
      case -1:
        return 'down';
      default:
        return '';
    }
  }

  getTrafficPattern(area: TakeoffLandArea): string {
    return TakeoffLandArea.formatTrafficPatternDirection(area.trafficPatDirFixed);
  }

  getMarkings(area: TakeoffLandArea): string {
    if (!area.markingsType) {
      return '';
    }
    if (area.markingsCondition !== null) {
      const condition = TakeoffLandArea.formatRunwayMarkingsCondition(area.markingsCondition);
      return area.markingsType + `, in ${condition} condition`;
    }
    return area.markingsType;
  }

  getMarkingsWarning(area: TakeoffLandArea): boolean {
    return area.markingsCondition && (
      area.markingsCondition === RunwayMarkingsCondition.Fair
      || area.markingsCondition === RunwayMarkingsCondition.Poor
    );
  }

  getApproachLightingType(lighting: ApproachLighting): string {
    return ApproachLighting.formatApproachLightingType(lighting.type);
  }

  getIlsType(area: TakeoffLandArea): string {
    return TakeoffLandArea.formatILSType(area.ilsType);
  }

  formatObstacle(obstacle: Obstacle): string {
    // Horizontal offset
    let hOffsetStr = '';
    if (obstacle.offsetDirection === 'Both-Sides') {
      hOffsetStr = obstacle.offsetDirection;
    } else if (obstacle.centerlineOffset && obstacle.offsetDirection) {
      const offset = this._decimalPipe.transform(obstacle.centerlineOffset);
      hOffsetStr = `${offset} ft ${obstacle.offsetDirection} ft of center`;
    }

    // Obstacle height
    let heightStr = '';
    if (obstacle.heightAgl) {
      const height = this._decimalPipe.transform(obstacle.heightAgl);
      heightStr = `${height} ft high`;
    }

    // Distance from end
    let distanceStr = '';
    if (obstacle.distanceFromEnd) {
      const distance = this._decimalPipe.transform(obstacle.distanceFromEnd);
      distanceStr = `${distance} ft from end`;
    }

    // Clearance slope
    let slopeStr = '';
    if (obstacle.clearanceSlope) {
      slopeStr = `${obstacle.clearanceSlope}:1 clearance slope`;
    }

    // Result
    const parts = [
      obstacle.obstacleType,
      hOffsetStr,
      heightStr,
      distanceStr,
      slopeStr
    ];
    return parts.filter(v => v).join(', ');
  }

  getRunwayRemarks(): Array<AirportComment> {
    return this.remarks.filter(c => c.commentType === CommentType.Runway && c.commentRef === this.runway.name);
  }

  getAreaRemarksFAA(area: TakeoffLandArea): Array<AirportComment> {
    return this.remarks.filter(c => c.commentType === CommentType.RunwayEnd && c.commentRef === area.name && c.sourceId === 1);
  }

  getAreaRemarksAM(area: TakeoffLandArea): Array<AirportComment> {
    return this.remarks.filter(c => c.commentType === CommentType.RunwayEnd && c.commentRef === area.name && c.sourceId === 5);
  }

  async reloadRemarks(identifier: string) {
    const airport = await this._airportService.getAirport(identifier, true);
    this.remarks = airport.airportComments;
  }

  async openEditRunwayDialog(runway: LandingSurface) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      airID: runway.airportId,
      runway: runway,
      id: null,
      isNew: false,
      windowTitle: 'Runway'
    };

    const dialogRef = this._editRunwayDialog.open(EditRunwayComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) {
        return;
      } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) {
        return;
      }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this.runway.surfaceReadable = isUpdated.runway.surfaceReadable;
        this._snackBar.open('Runway', 'Updated', {
          duration: 2000
        }); } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deleteRunway(runway: LandingSurface) {
    const dialogConfig = new MatDialogConfig();
    let ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      runwayId: runway.landingSurfaceId,
      airportId: runway.airportId
    };
    const dialogRef = this._deleteRunwayDialog.open(DeleteRunwayComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this.runwayDeleted.emit(runway.landingSurfaceId);
        this._snackBar.open('Runway', 'Deleted', {
          duration: 2000
        });
      } else {
        if (isUpdated.error != null) {
          ERROR_MSG = isUpdated.error.message;
        }
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  async openEditRunwayAreaDialog(takeoffLandArea: TakeoffLandArea) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      runwayId: this.runway.landingSurfaceId,
      airportId: this.runway.airportId,
      isNew: false,
      windowTitle: 'Runway Area'
    };

    takeoffLandArea == null ? dialogConfig.data.isNew = true : dialogConfig.data.takeoffLandArea = takeoffLandArea;

    const dialogRef = this._editTakeoffLandAreaDialog.open(EditTakeoffLandAreaComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        if (isUpdated.isNew) {
          this.runway.takeOffLandAreas.push(isUpdated.takeoffLandArea);
          this._snackBar.open('Runway Area', 'Added', {
            duration: 2000
          });
        } else {
          this._snackBar.open('Runway Area', 'Updated', {
            duration: 2000
          });
        }
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deleteTakeoffLandArea(takeoffLandArea: TakeoffLandArea) {
    const dialogConfig = new MatDialogConfig();
    let ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      takeoffLandAreaId: takeoffLandArea.takeoffLandAreaId,
      airportId: this.runway.airportId
    };
    const dialogRef = this._deleteTakeoffLandAreaDialog.open(DeleteTakeoffLandAreaComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        const index = this.runway.takeOffLandAreas.findIndex(n => n.takeoffLandAreaId === takeoffLandArea.takeoffLandAreaId);
        if (index > -1) {
          this.runway.takeOffLandAreas.splice(index, 1);
        }
        this._snackBar.open('Area', 'Deleted', {
          duration: 2000
        });
      } else {
        if (isUpdated.error != null) {
          ERROR_MSG = isUpdated.error.message;
        }
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  async openEditObstacleDialog(obstacle: Obstacle, area: TakeoffLandArea) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      takeoffLandAreaId: area.takeoffLandAreaId,
      airportId: this.runway.airportId,
      isNew: false,
      windowTitle: 'Obstacle'
    };

    obstacle == null ? dialogConfig.data.isNew = true : dialogConfig.data.obstacle = obstacle;

    const dialogRef = this._editObstacleDialog.open(EditAreaObstacleComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        if (isUpdated.isNew) {
          area.obstacles.push(isUpdated.obstacle);
          this._snackBar.open('Obstacle', 'Added', {
            duration: 2000
          });
        } else {
          this._snackBar.open('Obstacle', 'Updated', {
            duration: 2000
          });
        }
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deleteObstacle(obstacle: Obstacle, area: TakeoffLandArea) {
    const dialogConfig = new MatDialogConfig();
    let ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      obstacleId: obstacle.obstacleId,
      airportId: this.runway.airportId
    };
    const dialogRef = this._deleteTakeoffLandAreaDialog.open(DeleteAreaObstacleComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
          const index = area.obstacles.findIndex(o => o.obstacleId === obstacle.obstacleId);
          if (index > -1) {
            area.obstacles.splice(index, 1);
          }
        this._snackBar.open('Obstacle', 'Deleted', {
          duration: 2000
        });
      } else {
        if (isUpdated.error != null) {
          ERROR_MSG = isUpdated.error.message;
        }
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  async openEditLightingDialog(lighting: ApproachLighting, area: TakeoffLandArea) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      runwayId: this.runway.landingSurfaceId,
      airportId: this.runway.airportId,
      takeoffLandAreaId: area.takeoffLandAreaId,
      isNew: false,
      windowTitle: 'Runway Lighting'
    };

    lighting == null ? dialogConfig.data.isNew = true : dialogConfig.data.lighting = lighting;

    const dialogRef = this._editObstacleDialog.open(EditAreaLightingComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        if (isUpdated.isNew) {
          area.approachLighting.push(isUpdated.lighting);
          this._snackBar.open('Lighting', 'Added', {
            duration: 2000
          });
        } else {
          this._snackBar.open('Lighting', 'Updated', {
            duration: 2000
          });
        }
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deleteLighting(lighting: ApproachLighting, area: TakeoffLandArea) {
    const dialogConfig = new MatDialogConfig();
    let ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      lightingId: lighting.lightingId,
      airportId: this.runway.airportId
    };
    const dialogRef = this._deleteTakeoffLandAreaDialog.open(DeleteAreaLightingComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        const index = area.approachLighting.findIndex(l => l.lightingId === lighting.lightingId);
        if (index > -1) {
          area.approachLighting.splice(index, 1);
        }
        this._snackBar.open('Lighting', 'Deleted', {
          duration: 2000
        });
      } else {
        if (isUpdated.error != null) {
          ERROR_MSG = isUpdated.error.message;
        }
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  async openEditRunwayRemarkDialog(index: number, area: TakeoffLandArea) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    const defaultRemark = {
      commentType: AirportComment.parseCommentType('Runway-End'),
      commentRef: area.name
    };
    dialogConfig.data = {
      airID: this.airportId,
      id: null,
      isNew: false,
      remark: defaultRemark,
      windowTitle: 'Runway Comment'
    };
    index == null ? dialogConfig.data.isNew = true : dialogConfig.data.remark = this.getAreaRemarksAM(area)[index];
    dialogConfig.data['airID'] = this.airportId;
    dialogConfig.width = '60%';
    const dialogRef = this._editRemarkDialog.open(EditRemarkComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        if (isUpdated.isNew) {
          this.remarks.push(isUpdated.remark);
          this._snackBar.open('Runway Comment', 'Added', {
            duration: 2000
          });
        } else {
          this._snackBar.open('Runway Comment', 'Updated', {
            duration: 2000
          });
        }
        this.reloadRemarks(this.identifier);
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deleteRunwayRemark(index: number, area: TakeoffLandArea) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      airportId: this.airportId,
      remarkId: this.getAreaRemarksAM(area)[index].id
    };
    const dialogRef = this._deleteRemarkDialog.open(DeleteRemarkComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this._snackBar.open('Runway Comment', 'Deleted', {
          duration: 2000
        });
        // this.airport.airportComments.splice(index, 1);
        this.reloadRemarks(this.identifier);
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }
}
